import type { QueryClient, QueryKey } from '@tanstack/react-query';

import { SESSION_QUERY_KEY } from '@/features/auth/useSession';
import { PLATFORM_SESSION_KEY } from '@/features/platform/sessionKey';
import { ApiError } from '@/shared/api/client';
import type { PendingMfaState } from '@/shared/types/mfa';

import { mfaStateFromError, toPendingPayload } from './session';

/**
 * Any request answered `MFA_CHALLENGE_REQUIRED` or `MFA_ENROLMENT_REQUIRED`
 * sends its session back to the MFA step.
 *
 * The server re-evaluates the requirement on every request, so a session can
 * become pending mid-task — an administrator granted a role that needs MFA is
 * stopped at their next call. The cached session is marked pending at once (so
 * the shell gives way to the MFA screen instead of rendering refusals) and then
 * re-read from the server, which stays the authority on which step it is.
 *
 * The path decides WHICH session: the operator console and the tenant
 * workspace are separate guards with separate sessions, and one being pending
 * says nothing about the other. Supplier sessions have no MFA.
 */
type Surface = 'tenant' | 'platform';

function surfaceOf(error: ApiError): Surface | null {
  const raw = error.path ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  const path = raw.startsWith('/') ? raw : `/${raw}`;

  if (path.startsWith('/platform/') || path === '/platform') return 'platform';
  if (path.startsWith('/vendor/') || path === '/vendor') return null;

  return 'tenant';
}

function markPending(
  queryClient: QueryClient,
  key: QueryKey,
  state: PendingMfaState,
  enveloped: boolean,
): void {
  queryClient.setQueryData<unknown>(key, (current: unknown) => {
    if (current === undefined || current === null) return current;

    if (!enveloped) return toPendingPayload(current, state);

    const payload = (current as { data?: unknown }).data;

    return payload === undefined ? current : { data: toPendingPayload(payload, state) };
  });
  void queryClient.invalidateQueries({ queryKey: key, exact: true });
}

/** Returns whether the error was an MFA refusal (and has been handled). */
export function handleMfaRequired(queryClient: QueryClient, error: unknown): boolean {
  const state = mfaStateFromError(error);

  if (state === null || !(error instanceof ApiError)) return false;

  const surface = surfaceOf(error);

  if (surface === 'tenant') markPending(queryClient, SESSION_QUERY_KEY, state, false);
  if (surface === 'platform') markPending(queryClient, PLATFORM_SESSION_KEY, state, true);

  return surface !== null;
}

/**
 * Drops every cached read of one surface except its session.
 *
 * Called when the MFA step takes the screen, so nothing privileged read before
 * the session became pending stays in memory while it is.
 */
export function forgetSurfaceData(queryClient: QueryClient, surface: Surface): void {
  queryClient.removeQueries({
    predicate: (query) => {
      const [head, second] = query.queryKey as unknown[];

      if (surface === 'platform') return head === 'platform' && second !== 'session';

      return head !== SESSION_QUERY_KEY[0] && head !== 'platform';
    },
  });
}
